import Tooltip from "./tooltip";
import site from "../site";
import Helpers from "./helpers";
import Logger from "./logger";
import SlideToggle from "./slide-toggle";
import { IVariantData } from '../models/product-variant-types';
import { VariantSelectorElement } from "../../ui/components/product-variant-selector";
import AlgoliaTracking from "./algolia-tracking";
import Tracking from "./tracking";

declare var Mustache: any;

export default class VariantSelector {

	private selectorElement: VariantSelectorElement | null;
	private previousDeliveryData: any = null;
	private templates = {
		product: document.getElementById('productGalleryTemplate')?.innerHTML || '',
		spec: document.getElementById('productSpecificationTemplate')?.innerHTML || '',
		overlay: document.getElementById('overlayTemplate')?.innerHTML || '',
		stickyFloater: document.getElementById('stickyFloaterTemplate')?.innerHTML || '',
		stickyFloaterEstimate: document.getElementById('stickyFloaterEstimateTemplate')?.innerHTML || '',
		productDeliveryEstimateLoading: document.getElementById('productDeliveryEstimateLoadingTemplate')?.innerHTML || '',
		finance: document.getElementById('financeTemplate')?.innerHTML || '',
		popularRelatedCategories: document.getElementById('popularRelatedCategoriesTemplate')?.innerHTML || '',
		keyAttributes: document.getElementById('keyAttributesTemplate')?.innerHTML || ''
	}
	private targets = {
		product: document.getElementById('galleryContent'),
		spec: document.getElementById('specificationContent'),
		overlay: document.getElementById('overlay'),
		overlayStickyFloater: document.getElementById('overlayStickyFloater'),
		stickyFloater: document.getElementById('stickyFloater'),
		finance: document.getElementById('finance'),
		popularRelatedCategories: document.getElementById('popularRelatedCategories'),
		keyAttributes: document.getElementById('keyAttributesContent')
	}

	constructor(selectorElement: VariantSelectorElement) {
		this.selectorElement = selectorElement;
		if (!this.selectorElement) {
			return;
		}
		try {
			this.selectorElement.variants = JSON.parse(document.getElementById('jsProductVariants').innerHTML);
			this.selectorElement.variantOptionGroups = JSON.parse(document.getElementById('jsProductVariantsOptions').innerHTML);
			this.watchForVariantChanges();
		} catch (error) {
			Logger.trackException(error);
		}
	}

	private watchForVariantChanges() {

		this.selectorElement.addEventListener('variant-changed', (event: any) => {
			const { selectedOptions, currentVariant } = event.detail;
			// Defer DOM-heavy work (lots of the DOM gets updated on variant change!) to a macrotask after the browser has painted the interaction result
			setTimeout(() => {
				if (currentVariant?.Data) {
					this.updateProduct(currentVariant.Data as IVariantData, true);
				} else {
					const baseProductData = JSON.parse(document.getElementById('jsProductBase').innerHTML);
					this.updateProduct(baseProductData, false);
				}
			}, 0);
		});

	}

	private productHasChanged(product: IVariantData) {
		const currentProductSKUElement = document.getElementById('productSKU') as HTMLInputElement;
		if (currentProductSKUElement && currentProductSKUElement.value === product.Sku) {
			return false;
		}
		return true;
	}

	private updateProduct(product: IVariantData, variantIsSelected: boolean = true) {

		if (!this.productHasChanged(product)) {
			return;
		}

		// Gallery

		this.renderGallery(product);

		// Spec

		this.renderSpec(product);

		// Key attributes

		this.renderKeyAttributes(product);

		// Overlay

		this.renderOverlay(product);

		// Finance

		this.renderFinance(product);

		// Popular Related Categories

		this.renderPopularRelatedCategories(product);

		// Manual item updates

		this.renderManualChanges(product, variantIsSelected);

		// Sticky floater
		this.renderStickyFloater(product);

		// Related Products

		this.renderRelatedProducts(product.Sku);

		// Delivery Estimate

		(<any>window).deliveryEstimate.getEstimate(product.Sku).then((response) => {
			this.previousDeliveryData = response;
		});

		// Customers Also Bought

		this.renderCustomersAlsoBought(product.Sku);

		// Call required methods to reinitialise modules

		this.reInitOtherScripts();

		// Track in GA
		Tracking.trackViewItem(product.SelectedProductPrice.Price.Price, [product.ViewItemEventInfo]);

		if (!product.HasVariants) {
			AlgoliaTracking.viewedObjectIDs([product.Id], 'PDP: Product Viewed');
		}

	}

	private renderGallery(data: IVariantData) {
		try {
			var renderedGallery = Mustache.render(this.templates.product, data.Gallery);
			if (this.targets.product) {
				this.targets.product.innerHTML = renderedGallery;
			}
		} catch (error) {
			Logger.trackException(error);
		}
	}

	private renderSpec(data: IVariantData) {
		try {
			var renderedSpec = Mustache.render(this.templates.spec, data.Specification);
			if (this.targets.spec) {
				this.targets.spec.innerHTML = renderedSpec;
			}
		} catch (error) {
			Logger.trackException(error);
		}
	}

	private renderKeyAttributes(data: IVariantData) {
		try {
			var renderedKeyAttributes = Mustache.render(this.templates.keyAttributes, {
				HasKeyAttributes: data.KeyAttributes && data.KeyAttributes.length > 0,
				KeyAttributes: data.KeyAttributes,
				KeyAttributesCount: data.KeyAttributes ? data.KeyAttributes.length : 0,
				HasKeyFeatures: document.querySelector('.js-product-has-key-features') !== null // Key features does not change on variant change, so we check for the existence of the element on the page
			});
			if (this.targets.keyAttributes) {
				this.targets.keyAttributes.innerHTML = renderedKeyAttributes;
			}
		} catch (error) {
			Logger.trackException(error);
		}
	}

	private renderOverlay(data: IVariantData) {
		try {
			var overlayHTML = "";
			for (var i = 0; i < data.Overlays.length; i++) {
				overlayHTML += Mustache.render(this.templates.overlay, data.Overlays[i]);
			}
			if (this.targets.overlay) {
				this.targets.overlay.innerHTML = overlayHTML;
			}
		} catch (error) {
			Logger.trackException(error);
		}
	}

	private renderFinance(data: IVariantData) {
		try {
			var finance = Mustache.render(this.templates.finance, data.AvailableFinancePaymentMethods);
			if (this.targets.finance) {
				this.targets.finance.innerHTML = finance;
				if (data.AvailableFinancePaymentMethods.UseFinanceAccordion) {
					new SlideToggle();
				}
			}
		} catch (error) {
			Logger.trackException(error);
		}
	}

	private renderPopularRelatedCategories(data: IVariantData) {
		try {
			var popularRelatedCategories = Mustache.render(this.templates.popularRelatedCategories, data.RelatedCategories);
			if (this.targets.popularRelatedCategories) {
				this.targets.popularRelatedCategories.innerHTML = popularRelatedCategories;
			}
		} catch (error) {
			Logger.trackException(error);
		}
	}

	private renderStickyFloater(data: IVariantData) {
		try {
			var stickyFloaterData = JSON.parse(JSON.stringify(data));
			if (this.previousDeliveryData) {
				stickyFloaterData.DeliveryEstimate = this.previousDeliveryData.estimate;
			}
			var renderedStickyFloater = Mustache.render(this.templates.stickyFloater, stickyFloaterData);
			if (this.targets.stickyFloater) {
				this.targets.stickyFloater.innerHTML = renderedStickyFloater;
			}
		} catch (error) {
			Logger.trackException(error);
		}
	}

	private renderManualChanges(data: IVariantData, variantIsSelected: boolean = true) {

		const noVariantSelectedElement = document.getElementById('noVariantSelected') as HTMLInputElement;
		if (noVariantSelectedElement) {
			noVariantSelectedElement.value = (!data.IsPurchasable).toString();
		}

		const productAddElement = document.getElementById('productAdd') as HTMLButtonElement;
		if (productAddElement) {
			productAddElement.textContent = data.SubmitLabel;
			productAddElement.setAttribute('disabled', 'disabled');
			if (data.InStock) {
				productAddElement.removeAttribute('disabled');
			}
		}

		const productSKUElement = document.getElementById('productSKU') as HTMLInputElement;
		if (productSKUElement) {
			productSKUElement.value = data.Sku;
		}

		const productMainPriceElements = document.querySelectorAll('product-main-price');
		const productVariantSelect = document.getElementById('productVariants') as VariantSelectorElement;
		for (let i = 0; i < productMainPriceElements.length; i++) {
			const priceElement = productMainPriceElements[i] as any;
			priceElement.retailPrice = JSON.parse(data.SelectedProductPrice.RetailPriceJson);
			priceElement.originalPrice = JSON.parse(data.SelectedProductPrice.OriginalPriceJson);
			priceElement.rrp = JSON.parse(data.SelectedProductPrice.RRPJson);
			priceElement.price = JSON.parse(data.SelectedProductPrice.PriceJson);
			priceElement.percentageSaving = data.SelectedProductPrice.PercentSavingFormatted;
			priceElement.tradeSaving = data.SelectedProductPrice.TradeSaving;
			priceElement.hasVariants = data.HasVariants;
			priceElement.showSaleWasPrice = data.SelectedProductPrice.ShowSaleWasPrice;
			priceElement.variantIsSelected = productVariantSelect.isValid(false);
			priceElement.hideRrp = data.SelectedProductPrice.HideRrp;
		}

		var dropdownhtml = "";
		for (var s = 0; s < data.AvailableStock; s++) { dropdownhtml = dropdownhtml + "<option>" + (s + 1) + "</option>"; }
		const productQuantityElement = document.getElementById('productQuantity');
		if (productQuantityElement) {
			productQuantityElement.innerHTML = dropdownhtml;
		}

		const wishlistMainProductButtons = document.querySelectorAll('.js-add-to-wishlist-main');
		if (wishlistMainProductButtons.length) {
			for (let i = 0; i < wishlistMainProductButtons.length; i++) {
				wishlistMainProductButtons[i].setAttribute('data-wishlist-id', data.Sku);
			}
		}

		// Update product sample buttons
		setTimeout(() => {
			const productSampleElements = document.querySelectorAll('product-sample');
			for (let i = 0; i < productSampleElements.length; i++) {
				const productSampleElement = productSampleElements[i] as any;
				productSampleElement.id = variantIsSelected ? data.Sku : '';
			}
		}, 500);

		const triggerUpdateWishlistButtons = Helpers.createCustomEventForDispatch('updateWishlistButtons');
		document.dispatchEvent(triggerUpdateWishlistButtons);

	}

	private renderRelatedProducts(sku: string) {
		this.loadCarousel('you-may-also-like-products', '/api/relatedproducts/youmayalsolike?name=' + sku);
	}

	private renderCustomersAlsoBought(sku: string) {
		this.loadCarousel('customers-also-bought-products', "/api/customersalsobought?name=" + sku);
	}

	private loadCarousel(selector: string, url: string) {
		const containerElement = document.getElementById(selector);
		if (!containerElement) {
			return;
		}
		containerElement.setAttribute('data-load-content', url);
	}

	private reInitOtherScripts() {
		(<any>window).ProductPage.init();
		(<any>window).initProductCountdown();
		Tooltip.init(false);
		site.smoothScroll();
		(<any>window).slideToggle.init();
	}

}
